import React, { useEffect, useMemo, useRef, useState } from "react";
import "./simpleCalendar.style.scss";
import { getTodayEasternYMD, parseYMDDate } from "../../../utils/dateUtils.ts";

const pad2 = (value) => String(value).padStart(2, "0");

const formatDate = (date) => (
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
);

const parseDate = (value) => parseYMDDate(value);

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const dayNames = ["S", "M", "T", "W", "T", "F", "S"];

const SimpleCalendar = ({
  value,
  dateIn,
  onDateChange,
  placeholder = "YYYY-MM-DD",
  className = "",
  disabled = false,
  allowClear = true,
}) => {
  const wrapperRef = useRef(null);
  const selectedDate = useMemo(() => parseDate(value ?? dateIn), [value, dateIn]);
  const fallbackViewDate = selectedDate || parseYMDDate(getTodayEasternYMD()) || new Date();
  const [viewYear, setViewYear] = useState(fallbackViewDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(fallbackViewDate.getMonth());
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!selectedDate) return;
    setViewYear(selectedDate.getFullYear());
    setViewMonth(selectedDate.getMonth());
  }, [selectedDate]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen]);

  const calendarCells = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const last = new Date(viewYear, viewMonth + 1, 0);
    const blanks = Array.from({ length: first.getDay() }, () => null);
    const days = Array.from({ length: last.getDate() }, (_, idx) => idx + 1);
    return [...blanks, ...days];
  }, [viewYear, viewMonth]);

  const moveMonth = (delta) => {
    const next = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  };

  const selectDay = (day, event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!day) return;
    setIsOpen(false);
    onDateChange?.(formatDate(new Date(viewYear, viewMonth, day)));
  };

  const selectToday = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    const today = parseYMDDate(getTodayEasternYMD()) || new Date();
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    setIsOpen(false);
    onDateChange?.(formatDate(today));
  };

  const clearDate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen(false);
    onDateChange?.("");
  };

  const selectedValue = selectedDate ? formatDate(selectedDate) : "";
  const todayValue = getTodayEasternYMD();

  return (
    <div ref={wrapperRef} className={`simple-calendar ${className}`}>
      <button
        type="button"
        className={`simple-calendar__input ${isOpen ? "is-open" : ""}`}
        disabled={disabled}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        <span className={selectedValue ? "" : "simple-calendar__placeholder"}>
          {selectedValue || placeholder}
        </span>
        <span className="simple-calendar__icon" aria-hidden="true">cal</span>
      </button>

      {allowClear && selectedValue && (
        <button
          type="button"
          className="simple-calendar__clear"
          disabled={disabled}
          onClick={clearDate}
          aria-label="Clear date"
        >
          x
        </button>
      )}

      {isOpen && !disabled && (
        <div
          className="simple-calendar__popup"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="simple-calendar__header">
            <button type="button" onClick={(event) => { event.preventDefault(); event.stopPropagation(); moveMonth(-1); }} aria-label="Previous month">
              {"<"}
            </button>
            <div className="simple-calendar__month">
              <strong>{monthNames[viewMonth]}</strong>
              <span>{viewYear}</span>
            </div>
            <button type="button" onClick={(event) => { event.preventDefault(); event.stopPropagation(); moveMonth(1); }} aria-label="Next month">
              {">"}
            </button>
          </div>

          <div className="simple-calendar__weekdays">
            {dayNames.map((day, idx) => (
              <span key={`${day}-${idx}`}>{day}</span>
            ))}
          </div>

          <div className="simple-calendar__grid">
            {calendarCells.map((day, idx) => {
              const cellDate = day ? formatDate(new Date(viewYear, viewMonth, day)) : "";
              return (
                <button
                  type="button"
                  key={`${day || "blank"}-${idx}`}
                  className={[
                    "simple-calendar__day",
                    !day ? "is-blank" : "",
                    cellDate === selectedValue ? "is-selected" : "",
                    cellDate === todayValue ? "is-today" : "",
                  ].filter(Boolean).join(" ")}
                  disabled={!day}
                  onClick={(event) => selectDay(day, event)}
                >
                  {day || ""}
                </button>
              );
            })}
          </div>

          <div className="simple-calendar__footer">
            <button type="button" onClick={selectToday}>Today</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleCalendar;
